import { MdClose } from "react-icons/md";

import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { getLineColor, getShadowColor } from "@/helpers/helperFunctions";

import { useFlowContext } from "@/components/blocks/flow/StrategyViewModal";
import Handles from "@/components/blocks/flow/Handles";
import InputCard from "@/components/blocks/flow/InputCard";
import { BlocksType } from "@/components/blocks/flow/types";
import { Badge } from "@/components/ui/badge";
import { usePairCheck } from "./usePairCheck";
import SymbolSelector from "../test/SymbolSelector/SymbolSelector";

const renderTooltipContent = (title: string, type: string) => {
  return type === BlocksType.DATA && title === "LTP"
    ? "Current price"
    : title.replace("/", " / ");
};

const CustomSelectorNode = ({
  id,
  data,
}: {
  data: any;
  [key: string]: any;
}) => {
  const { edges, nodes, showValues } = useFlowContext();
  const isPairBlockConnected = usePairCheck({
    port: data?.inputs?.[0],
    type: data?.type,
    data: data,
  });
  return (
    <Card
      data-id={id + "1"}
      className={cn(
        "max-w-xs w-screen  bg-[#0F172A] relative flex flex-col transition-all duration-300 border-white/20 h-auto",
      )}
      style={
        isPairBlockConnected?.isPairBlock
          ? getShadowColor(
              isPairBlockConnected?.isPairBlock
                ? BlocksType.PAIR_BLOCK
                : data?.type,
              data?.title,
              isPairBlockConnected?.pairColor,
            )
          : {}
      }
    >
      <CardHeader className="p-4">
        <div className="flex items-start justify-between w-full ">
          {/* LEFT SIDE: Bar + Text */}
          <div className="flex gap-3 items-start flex-1 min-w-0 ">
            {/* Vertical colored pill */}
            <div
              className="w-1.5 h-10 rounded-full shrink-0 mt-0.5"
              style={{
                backgroundColor: getLineColor(data?.type, data?.title),
              }}
            />

            {/* Title and Description */}
            <div className="flex flex-col min-w-0 flex-1 ">
              <div className="flex items-center gap-2">
                <Tooltip delayDuration={100}>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 max-w-[170px]">
                      <span className="min-w-0 overflow-hidden whitespace-nowrap text-ellipsis text-[1.1rem] font-bold tracking-tight">
                        {renderTooltipContent(data?.title || "", data.type)}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {renderTooltipContent(data?.title || "", data.type)}
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="text-[0.7rem]  text-muted-foreground font-medium truncate mt-0.5 max-w-[170px]">
                {data.type}
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Actions */}
          <div className="flex flex-col items-end">
            <div className="flex flex-shrink-0 items-center justify-end gap-1 text-muted-foreground ">
              {data?.title !== "Price Data" && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-fit w-fit p-0 text-foreground"
                    >
                      <MdClose size={18} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete Block</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        <div className="w-full h-[1px] bg-border/50 mt-4" />
      </CardHeader>
      {isPairBlockConnected?.pairSymbol && (
        <Badge className="absolute top-[-10px] right-2 bg-[--accent-primary] text-white">
          {isPairBlockConnected?.pairSymbol}
        </Badge>
      )}
      <div className="flex w-full h-auto px-4 mt-2">
        <Handles data={data} edges={edges} nodes={nodes} id={id} />
      </div>
      {showValues && (
        <CardContent className="pt-2">
          <div>{<InputCard data={data} id={id} disabled />}</div>
        </CardContent>
      )}
      {data.type == BlocksType.PAIR_BLOCK && (
        <CardContent className="pt-2">
          <SymbolSelector
            broker={data?.broker_id ?? "4"}
            selectedSymbols={[data?.other_inputs?.[0]?.value]}
            onSelected={() => {}}
            isSingle
            disabled
          />
        </CardContent>
      )}
    </Card>
  );
};

export default CustomSelectorNode;
